import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { POPOVER_SHELL_CLASS } from './constants';
import type { FloatingMenu } from './useFloatingMenu';

interface FloatingPopoverProps {
  menu: FloatingMenu;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}

// The portal + fixed-positioning + hover-handling shell shared by every sidebar
// popover (collapsed nav items, collapsed expandables, the user menu). Callers
// supply only the surface classes and the content; positioning comes from the
// useFloatingMenu controller passed in.
//
// The outer element is the shell. It owns the three things a portalled popover
// must not be able to get wrong — its stacking tier, its font, and the hover
// bridge that holds it open while the pointer crosses over — because portalling
// to document.body escapes the sidebar's stacking context and font scope alike.
// They live here rather than in each caller's class string so that no call site
// can omit them; deliberately not overridable.
export function FloatingPopover({ menu, className, style, children }: FloatingPopoverProps) {
  if (!menu.open) return null;

  return createPortal(
    <div
      ref={menu.floatingRef}
      data-sidebar-popover=""
      className={POPOVER_SHELL_CLASS}
      style={{ position: 'fixed', top: menu.coords.y, left: menu.coords.x, ...menu.bridgeStyle }}
      onMouseEnter={menu.show}
      onMouseLeave={menu.scheduleHide}
    >
      <div className={className} style={style}>
        {children}
      </div>
    </div>,
    document.body,
  );
}
